import React, { useEffect, useState } from 'react';
import api from '../lib/api';
import { Plus, Trash } from '@phosphor-icons/react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Progress } from '../components/ui/progress';

const CATEGORIES = ['Food', 'Transport', 'Housing', 'Utilities', 'Entertainment', 'Shopping', 'Health', 'Education', 'Insurance', 'Other'];

const fmt = (n) => `Rs. ${new Intl.NumberFormat('en-NP').format(Math.round(n || 0))}`;

export default function BudgetsPage() {
  const [budgets, setBudgets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));
  const [form, setForm] = useState({ category: '', limit_amount: '', month: new Date().toISOString().slice(0, 7) });

  const load = () => {
    api.get('/budgets', { params: { month } }).then(r => setBudgets(r.data)).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(load, [month]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await api.post('/budgets', { ...form, limit_amount: parseFloat(form.limit_amount) });
      setDialogOpen(false);
      setForm({ category: '', limit_amount: '', month });
      load();
    } catch {}
  };

  const handleDelete = async (id) => {
    await api.delete(`/budgets/${id}`);
    load();
  };

  const totalBudget = budgets.reduce((s, b) => s + b.limit_amount, 0);
  const totalSpent = budgets.reduce((s, b) => s + (b.spent || 0), 0);

  return (
    <div className="space-y-6 animate-fade-in" data-testid="budgets-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold tracking-tight text-[#1C1B1A]" style={{ fontFamily: 'Manrope, sans-serif' }}>Budgets</h1>
          <p className="text-[#5C5A56] text-sm mt-1">Set spending limits by category</p>
        </div>
        <div className="flex items-center gap-3">
          <input
            data-testid="month-picker"
            type="month"
            value={month}
            onChange={(e) => setMonth(e.target.value)}
            className="bg-white border border-[#E8E6E1] text-[#1C1B1A] rounded-lg px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1E3F2D]/20"
          />
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <button data-testid="add-budget-btn" className="bg-[#1E3F2D] text-white font-semibold rounded-xl px-5 py-2.5 hover:bg-[#29563E] transition-colors active:scale-95 flex items-center gap-2 text-sm">
                <Plus size={18} weight="bold" /> Set Budget
              </button>
            </DialogTrigger>
            <DialogContent className="bg-white border-[#E8E6E1] rounded-2xl">
              <DialogHeader>
                <DialogTitle className="text-xl font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>Set Budget</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4 mt-2">
                <div>
                  <label className="block text-xs font-bold uppercase tracking-[0.2em] text-[#5C5A56] mb-2">Category</label>
                  <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                    <SelectTrigger className="w-full border-[#E8E6E1] rounded-lg" data-testid="budget-category-select">
                      <SelectValue placeholder="Select category" />
                    </SelectTrigger>
                    <SelectContent>
                      {CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="block text-xs font-bold uppercase tracking-[0.2em] text-[#5C5A56] mb-2">Budget Limit</label>
                  <input data-testid="budget-limit-input" type="number" step="0.01" value={form.limit_amount} onChange={(e) => setForm({ ...form, limit_amount: e.target.value })}
                    className="w-full bg-white border border-[#E8E6E1] text-[#1C1B1A] rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#1E3F2D]/20 focus:border-[#1E3F2D] transition-all" placeholder="500" required />
                </div>
                <div>
                  <label className="block text-xs font-bold uppercase tracking-[0.2em] text-[#5C5A56] mb-2">Month</label>
                  <input data-testid="budget-month-input" type="month" value={form.month} onChange={(e) => setForm({ ...form, month: e.target.value })}
                    className="w-full bg-white border border-[#E8E6E1] text-[#1C1B1A] rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#1E3F2D]/20 focus:border-[#1E3F2D] transition-all" required />
                </div>
                <button data-testid="submit-budget-btn" type="submit" className="w-full bg-[#1E3F2D] text-white font-semibold rounded-xl px-6 py-3 hover:bg-[#29563E] transition-colors active:scale-95">
                  Set Budget
                </button>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Overview */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="bg-white border border-[#E8E6E1] rounded-2xl shadow-sm p-5">
          <p className="text-xs font-bold uppercase tracking-[0.15em] text-[#5C5A56] mb-1">Total Budget</p>
          <p className="text-2xl font-bold font-mono-numbers text-[#1C1B1A]" data-testid="total-budget">{fmt(totalBudget)}</p>
        </div>
        <div className="bg-white border border-[#E8E6E1] rounded-2xl shadow-sm p-5">
          <p className="text-xs font-bold uppercase tracking-[0.15em] text-[#5C5A56] mb-1">Total Spent</p>
          <p className="text-2xl font-bold font-mono-numbers text-[#A63C24]" data-testid="total-spent">{fmt(totalSpent)}</p>
        </div>
        <div className="bg-white border border-[#E8E6E1] rounded-2xl shadow-sm p-5">
          <p className="text-xs font-bold uppercase tracking-[0.15em] text-[#5C5A56] mb-1">Remaining</p>
          <p className={`text-2xl font-bold font-mono-numbers ${totalBudget - totalSpent >= 0 ? 'text-[#265C42]' : 'text-[#A63C24]'}`} data-testid="remaining-budget">
            {fmt(totalBudget - totalSpent)}
          </p>
        </div>
      </div>

      {/* Budget cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="budget-list">
        {loading ? (
          <div className="col-span-2 p-8 text-center"><div className="w-6 h-6 border-2 border-[#1E3F2D] border-t-transparent rounded-full animate-spin mx-auto" /></div>
        ) : budgets.length === 0 ? (
          <div className="col-span-2 bg-white border border-[#E8E6E1] rounded-2xl shadow-sm p-8 text-center text-[#5C5A56] text-sm">
            No budgets set for this month. Create one to start tracking!
          </div>
        ) : (
          budgets.map(b => {
            const pct = b.limit_amount > 0 ? Math.min((b.spent || 0) / b.limit_amount * 100, 100) : 0;
            const overBudget = (b.spent || 0) > b.limit_amount;
            return (
              <div key={b.id || b.category} className="bg-white border border-[#E8E6E1] rounded-2xl shadow-sm p-5 hover:shadow-md transition-all duration-300" data-testid={`budget-${b.category}`}>
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <p className="text-base font-semibold text-[#1C1B1A]">{b.category}</p>
                    {overBudget && <span className="text-xs text-[#A63C24] font-semibold">Over budget!</span>}
                  </div>
                  <button onClick={() => handleDelete(b.id)} className="text-[#5C5A56] hover:text-[#A63C24] transition-colors p-1">
                    <Trash size={16} />
                  </button>
                </div>
                <div className="flex justify-between text-sm mb-2">
                  <span className="text-[#5C5A56]">Spent: <span className="font-mono-numbers font-semibold text-[#1C1B1A]">{fmt(b.spent || 0)}</span></span>
                  <span className="text-[#5C5A56]">of <span className="font-mono-numbers font-semibold">{fmt(b.limit_amount)}</span></span>
                </div>
                <Progress value={pct} className={`h-2 ${overBudget ? '[&>div]:bg-[#A63C24]' : '[&>div]:bg-[#265C42]'}`} />
                <p className="text-xs text-[#5C5A56] mt-2 font-mono-numbers">{pct.toFixed(0)}% used</p>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
